import { CliError, ExitCode } from './errors.js';

export type CommandPolicy = 'read-only' | 'interactive' | 'unrestricted';

export interface GlobalOptions {
  device?: string;
  json: boolean;
  jsonl: boolean;
  pretty: boolean;
  timeoutSeconds: number;
  configPath?: string;
  credentialsPath?: string;
  trace: boolean;
  agent: boolean;
  policy: CommandPolicy;
  maxItems?: number;
  fields?: string[];
}

export interface ParsedArgs {
  command: string[];
  options: GlobalOptions;
  flags: Map<string, string[]>;
  positionals: string[];
}

const DEFAULT_TIMEOUT_SECONDS = 30;

const BOOLEAN_FLAGS = new Set([
  'json',
  'jsonl',
  'pretty',
  'no-pretty',
  'trace',
  'help',
  'version',
  'all',
  'rebuild',
  'force',
  'include-read-only',
  'clear-launcher',
  'clear-pin',
  'clear-limits',
  'regenerate-switch-id',
  'all-pages',
  'watch',
  'yes',
  'unsafe',
  'agent',
  'summary',
]);

const KNOWN_FLAGS = new Set([
  ...BOOLEAN_FLAGS,
  'device',
  'timeout',
  'config',
  'credentials-path',
  'action',
  'add',
  'arg',
  'audio-scan-feedback',
  'audio-volume',
  'backup-remote-enabled',
  'backup-remote-schedule',
  'buttons',
  'category',
  'choice-id',
  'client',
  'claim-url',
  'client-id',
  'cursor',
  'daily',
  'daily-limit',
  'data',
  'debug-logging',
  'enabled',
  'encryption',
  'error-reporting',
  'fields',
  'filter',
  'fuzzy-system',
  'group',
  'id',
  'image-type',
  'label',
  'last',
  'launch-guard-delay',
  'launch-guard-enabled',
  'launch-guard-require-confirm',
  'launch-guard-timeout',
  'launcher',
  'letter',
  'limit',
  'limits-enabled',
  'match',
  'max-items',
  'max-pages',
  'max-results',
  'max-size',
  'media-id',
  'media-name',
  'media-path',
  'methods',
  'name',
  'output',
  'override',
  'params',
  'path',
  'pattern',
  'pin',
  'playtime-sync-enabled',
  'policy',
  'profile',
  'profile-id',
  'profiles-require-for-launch',
  'profiles-swap-data',
  'reader',
  'readers-auto-detect',
  'readers-connect',
  'readers-scan-exit-delay',
  'readers-scan-ignore-system',
  'readers-scan-mode',
  'remove',
  'retention',
  'role',
  'run-zapscript',
  'scraper',
  'seconds',
  'session',
  'session-limit',
  'session-reset',
  'since',
  'skill',
  'slot',
  'sort',
  'switch-id',
  'system',
  'system-defaults',
  'system-id',
  'tag',
  'target',
  'token',
  'type',
  'uid',
  'until',
  'update-channel',
  'url',
  'warning',
]);

function takeValue(argv: string[], index: number, flag: string): string {
  const value = argv[index + 1];
  if (value === undefined || value.startsWith('--')) {
    throw new CliError(`Missing value for --${flag}`, ExitCode.Usage);
  }
  return value;
}

export function parseCliArgs(argv: string[]): ParsedArgs {
  const flags = new Map<string, string[]>();
  const positionals: string[] = [];
  const options: GlobalOptions = {
    json: false,
    jsonl: false,
    pretty: true,
    timeoutSeconds: DEFAULT_TIMEOUT_SECONDS,
    trace: false,
    agent: false,
    policy: 'interactive',
  };

  for (let i = 0; i < argv.length; i++) {
    const arg = argv[i];
    if (!arg.startsWith('--')) {
      positionals.push(arg);
      continue;
    }

    const raw = arg.slice(2);
    const [name, inline] = raw.split(/=(.*)/s).filter((part) => part !== undefined);
    if (!name || !KNOWN_FLAGS.has(name)) {
      throw new CliError(`Unknown option --${name}`, ExitCode.Usage);
    }
    let value = inline;
    if (!BOOLEAN_FLAGS.has(name) && value === undefined) {
      value = takeValue(argv, i, name);
      i++;
    }
    if (value === undefined) value = 'true';

    const existing = flags.get(name) ?? [];
    existing.push(value);
    flags.set(name, existing);

    switch (name) {
      case 'device':
        options.device = value;
        break;
      case 'json':
        options.json = true;
        break;
      case 'jsonl':
        options.jsonl = true;
        break;
      case 'pretty':
        options.pretty = true;
        break;
      case 'no-pretty':
        options.pretty = false;
        break;
      case 'timeout': {
        const parsed = Number(value);
        if (!Number.isFinite(parsed) || parsed <= 0) {
          throw new CliError('--timeout must be a positive number of seconds', ExitCode.Usage);
        }
        options.timeoutSeconds = parsed;
        break;
      }
      case 'config':
        options.configPath = value;
        break;
      case 'credentials-path':
        options.credentialsPath = value;
        break;
      case 'trace':
        options.trace = true;
        break;
      case 'agent':
        options.agent = true;
        options.json = true;
        options.pretty = false;
        break;
      case 'help':
        break;
    }
  }

  const explicitPolicy = flag(flags, 'policy');
  const environmentPolicy = options.agent ? undefined : process.env.ZAPAROO_POLICY;
  options.policy = parsePolicy(
    explicitPolicy ?? environmentPolicy,
    options.agent,
    explicitPolicy !== undefined
      ? '--policy'
      : environmentPolicy !== undefined
        ? 'ZAPAROO_POLICY'
        : undefined,
  );
  const maxItems = numberFlag(flags, 'max-items');
  if (maxItems !== undefined) {
    if (!Number.isInteger(maxItems) || maxItems < 1 || maxItems > 10_000) {
      throw new CliError('--max-items must be an integer between 1 and 10000', ExitCode.Usage);
    }
    options.maxItems = maxItems;
  } else if (options.agent) {
    options.maxItems = 50;
  }
  const fields = flag(flags, 'fields');
  if (fields !== undefined) {
    const selected = fields
      .split(',')
      .map((field) => field.trim())
      .filter(Boolean);
    if (selected.length === 0) {
      throw new CliError('--fields requires a comma-separated field list', ExitCode.Usage);
    }
    options.fields = selected;
  }

  return {
    command: positionals.slice(0, 3),
    options,
    flags,
    positionals,
  };
}

export function flag(flags: Map<string, string[]>, name: string): string | undefined {
  return flags.get(name)?.at(-1);
}

export function flagAll(flags: Map<string, string[]>, name: string): string[] {
  return flags.get(name) ?? [];
}

export function hasFlag(flags: Map<string, string[]>, name: string): boolean {
  return flags.has(name);
}

export function numberFlag(flags: Map<string, string[]>, name: string): number | undefined {
  const value = flag(flags, name);
  if (value === undefined) return undefined;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) throw new CliError(`--${name} must be a number`, ExitCode.Usage);
  return parsed;
}

function parsePolicy(
  value: string | undefined,
  agent: boolean,
  source?: '--policy' | 'ZAPAROO_POLICY',
): CommandPolicy {
  const policy = value ?? (agent ? 'read-only' : 'interactive');
  if (policy === 'read-only' || policy === 'interactive' || policy === 'unrestricted') {
    return policy;
  }
  throw new CliError(
    `${source ?? '--policy'} must be read-only, interactive, or unrestricted`,
    ExitCode.Usage,
  );
}

export function booleanFlag(flags: Map<string, string[]>, name: string): boolean | undefined {
  const value = flag(flags, name);
  if (value === undefined) return undefined;
  if (['true', '1', 'yes', 'on'].includes(value)) return true;
  if (['false', '0', 'no', 'off'].includes(value)) return false;
  throw new CliError(`--${name} must be true or false`, ExitCode.Usage);
}
